import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { 
  getFighters, 
  getTrainingCamps, 
  getFighterPDF, 
  getGymOverviewPDF, 
  getAttendancePDF, 
  getCampPDF,
  exportFightersCSV,
  exportAssessmentsCSV,
  exportAttendanceCSV,
  exportFightsCSV,
  exportCampsCSV,
  exportWeightCSV,
  exportMembersCSV,
  trackFeatureUsage 
} from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { 
  FileText,
  Download,
  Users,
  Building2,
  Calendar,
  UserCheck,
  Loader2,
  Table,
  Scale,
  Swords,
  ClipboardList,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const ReportCard = ({ title, description, icon: Icon, children, color = "red" }) => (
  <div className="bg-ba-surface border border-ba-border p-6 rounded-sm hover:border-red-600/30 transition-all">
    <div className="flex items-start gap-4 mb-4">
      <div className={cn(
        "w-12 h-12 rounded-sm flex items-center justify-center",
        color === "red" ? "bg-red-600/10 border border-red-600/20" :
        color === "blue" ? "bg-blue-600/10 border border-blue-600/20" :
        color === "green" ? "bg-green-600/10 border border-green-600/20" :
        "bg-purple-600/10 border border-purple-600/20"
      )}>
        <Icon className={cn(
          "w-6 h-6",
          color === "red" ? "text-red-500" :
          color === "blue" ? "text-blue-500" :
          color === "green" ? "text-green-500" :
          "text-purple-500"
        )} />
      </div>
      <div className="flex-1">
        <h3 className="font-heading text-xl text-white tracking-tight">{title}</h3>
        <p className="text-zinc-500 text-sm font-body mt-1">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

export const ReportsPage = () => {
  const { user } = useAuth();
  const [fighters, setFighters] = useState([]);
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pdf');
  
  // Report selections
  const [selectedFighter, setSelectedFighter] = useState('');
  const [selectedCamp, setSelectedCamp] = useState('');
  const [selectedAssessmentFighter, setSelectedAssessmentFighter] = useState('');
  const [assessmentType, setAssessmentType] = useState('all');
  const [assessmentGiNogi, setAssessmentGiNogi] = useState('all');
  const [attendanceStart, setAttendanceStart] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [attendanceEnd, setAttendanceEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [csvAttendanceStart, setCsvAttendanceStart] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [csvAttendanceEnd, setCsvAttendanceEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Download states
  const [downloadingFighter, setDownloadingFighter] = useState(false);
  const [downloadingGym, setDownloadingGym] = useState(false);
  const [downloadingAttendance, setDownloadingAttendance] = useState(false);
  const [downloadingCamp, setDownloadingCamp] = useState(false);
  const [downloadingAssessment, setDownloadingAssessment] = useState(false);
  
  // CSV download states
  const [csvDownloading, setCsvDownloading] = useState({});

  useEffect(() => {
    loadData();
    trackFeatureUsage('reports', 'view').catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      const [fightersRes, campsRes] = await Promise.all([
        getFighters(),
        getTrainingCamps()
      ]);
      setFighters(fightersRes.data);
      setCamps(campsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (url, filename, setDownloading) => {
    setDownloading(true);
    try {
      // Get auth token
      const token = localStorage.getItem('token');
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Report downloaded!');
      trackFeatureUsage('reports', 'download', { type: filename }).catch(() => {});
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const handleFighterDownload = () => {
    if (!selectedFighter) {
      toast.error('Please select a fighter');
      return;
    }
    const fighter = fighters.find(f => f.id === selectedFighter);
    downloadPDF(
      getFighterPDF(selectedFighter),
      `fighter_${fighter?.name?.replace(/\s+/g, '_') || 'report'}.pdf`,
      setDownloadingFighter
    );
  };

  const handleGymDownload = () => {
    downloadPDF(
      getGymOverviewPDF(),
      `gym_overview_${format(new Date(), 'yyyyMMdd')}.pdf`,
      setDownloadingGym
    );
  };

  const handleAttendanceDownload = () => {
    downloadPDF(
      getAttendancePDF(attendanceStart, attendanceEnd),
      `attendance_${attendanceStart}_to_${attendanceEnd}.pdf`,
      setDownloadingAttendance
    );
  };

  const handleCampDownload = () => {
    if (!selectedCamp) {
      toast.error('Please select a training camp');
      return;
    }
    const camp = camps.find(c => c.id === selectedCamp);
    downloadPDF(
      getCampPDF(selectedCamp),
      `camp_${camp?.name?.replace(/\s+/g, '_') || 'report'}.pdf`,
      setDownloadingCamp
    );
  };

  const handleAssessmentDownload = () => {
    if (!selectedAssessmentFighter) {
      toast.error('Please select a fighter');
      return;
    }
    const fighter = fighters.find(f => f.id === selectedAssessmentFighter);
    let url = `${API}/reports/assessment/${selectedAssessmentFighter}/pdf`;
    const params = [];
    if (assessmentType !== 'all') params.push(`assessment_type=${assessmentType}`);
    if (assessmentType === 'bjj' && assessmentGiNogi !== 'all') params.push(`gi_nogi=${assessmentGiNogi}`);
    if (params.length > 0) url += '?' + params.join('&');
    
    downloadPDF(
      url,
      `assessment_${fighter?.name?.replace(/\s+/g, '_') || 'report'}_${assessmentType}.pdf`,
      setDownloadingAssessment
    );
  };

  const downloadCSV = async (url, filename, key) => {
    setCsvDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('CSV exported successfully!');
      trackFeatureUsage('csv_export', 'download', { type: key }).catch(() => {});
    } catch (error) {
      toast.error(error.message || 'Failed to export CSV');
    } finally {
      setCsvDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Export & Download</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
            REPORTS & DATA EXPORT
          </h1>
          <p className="text-zinc-500 mt-2 font-body">Generate PDF reports or export your data to CSV/Excel</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-ba-surface border border-ba-border p-1 w-full sm:w-auto">
            <TabsTrigger 
              value="pdf" 
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-heading uppercase tracking-wider"
            >
              <FileText className="w-4 h-4 mr-2" />
              PDF Reports
            </TabsTrigger>
            <TabsTrigger 
              value="csv" 
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-heading uppercase tracking-wider"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              CSV Export
            </TabsTrigger>
          </TabsList>

          {/* PDF Reports Tab */}
          <TabsContent value="pdf" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Fighter Report */}
              <ReportCard
                title="FIGHTER PROFILE"
                description="Detailed report including stats, assessments, fight history, and weight tracking"
                icon={Users}
                color="red"
              >
                <div className="space-y-4">
                  <Select value={selectedFighter} onValueChange={setSelectedFighter}>
                    <SelectTrigger className="h-12 bg-ba-black border-ba-border" data-testid="fighter-report-select">
                      <SelectValue placeholder="Select a fighter..." />
                    </SelectTrigger>
                    <SelectContent>
                      {fighters.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name} - {f.weight_class}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleFighterDownload}
                    disabled={!selectedFighter || downloadingFighter}
                    className="w-full bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
                    data-testid="download-fighter-btn"
                  >
                    {downloadingFighter ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Download Fighter Report</>
                    )}
                  </Button>
                </div>
              </ReportCard>

              {/* Gym Overview Report */}
              <ReportCard
                title="GYM OVERVIEW"
                description="Complete gym statistics, fighter roster, and performance metrics"
                icon={Building2}
                color="blue"
              >
                <div className="space-y-4">
                  <div className="p-4 bg-ba-black rounded-sm border border-ba-border">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-500">Total Fighters</span>
                      <span className="text-white font-mono">{fighters.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-zinc-500">Active Camps</span>
                      <span className="text-white font-mono">{camps.filter(c => c.status === 'active').length}</span>
                    </div>
                  </div>
                  <Button
                    onClick={handleGymDownload}
                    disabled={downloadingGym}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white uppercase font-heading tracking-wider transition-all"
                    data-testid="download-gym-btn"
                  >
                    {downloadingGym ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Download Gym Report</>
                    )}
                  </Button>
                </div>
              </ReportCard>

              {/* Assessment Report - NEW */}
              <ReportCard
                title="ASSESSMENT REPORT"
                description="Detailed skill assessments with MMA/BJJ filtering and progress tracking"
                icon={ClipboardList}
                color="red"
              >
                <div className="space-y-4">
                  <Select value={selectedAssessmentFighter} onValueChange={setSelectedAssessmentFighter}>
                    <SelectTrigger className="h-12 bg-ba-black border-ba-border" data-testid="assessment-fighter-select">
                      <SelectValue placeholder="Select a fighter..." />
                    </SelectTrigger>
                    <SelectContent>
                      {fighters.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name} - {f.weight_class}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Sport Type</label>
                      <Select value={assessmentType} onValueChange={setAssessmentType}>
                        <SelectTrigger className="h-10 bg-ba-black border-ba-border" data-testid="assessment-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Assessments</SelectItem>
                          <SelectItem value="mma">MMA Only</SelectItem>
                          <SelectItem value="bjj">BJJ Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {assessmentType === 'bjj' && (
                      <div className="space-y-1">
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Gi / No-Gi</label>
                        <Select value={assessmentGiNogi} onValueChange={setAssessmentGiNogi}>
                          <SelectTrigger className="h-10 bg-ba-black border-ba-border" data-testid="assessment-gi-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="gi">Gi Only</SelectItem>
                            <SelectItem value="nogi">No-Gi Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    onClick={handleAssessmentDownload}
                    disabled={!selectedAssessmentFighter || downloadingAssessment}
                    className="w-full bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
                    data-testid="download-assessment-btn"
                  >
                    {downloadingAssessment ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Download Assessment Report</>
                    )}
                  </Button>
                </div>
              </ReportCard>

              {/* Attendance Report */}
              <ReportCard
                title="ATTENDANCE REPORT"
                description="Check-in history, top attendees, and attendance statistics"
                icon={UserCheck}
                color="green"
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Start Date</label>
                      <Input
                        type="date"
                        value={attendanceStart}
                        onChange={(e) => setAttendanceStart(e.target.value)}
                        className="h-10 bg-ba-black border-ba-border"
                        data-testid="attendance-start-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">End Date</label>
                      <Input
                        type="date"
                        value={attendanceEnd}
                        onChange={(e) => setAttendanceEnd(e.target.value)}
                        className="h-10 bg-ba-black border-ba-border"
                        data-testid="attendance-end-date"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleAttendanceDownload}
                    disabled={downloadingAttendance}
                    className="w-full bg-green-600 hover:bg-green-700 text-white uppercase font-heading tracking-wider transition-all"
                    data-testid="download-attendance-btn"
                  >
                    {downloadingAttendance ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Download Attendance Report</>
                    )}
                  </Button>
                </div>
              </ReportCard>

              {/* Training Camp Report */}
              <ReportCard
                title="TRAINING CAMP"
                description="Camp details, focus areas, notes, and fighter information"
                icon={Calendar}
                color="purple"
              >
                <div className="space-y-4">
                  <Select value={selectedCamp} onValueChange={setSelectedCamp}>
                    <SelectTrigger className="h-12 bg-ba-black border-ba-border" data-testid="camp-report-select">
                      <SelectValue placeholder="Select a training camp..." />
                    </SelectTrigger>
                    <SelectContent>
                      {camps.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} ({c.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleCampDownload}
                    disabled={!selectedCamp || downloadingCamp}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white uppercase font-heading tracking-wider transition-all"
                    data-testid="download-camp-btn"
                  >
                    {downloadingCamp ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Download Camp Report</>
                    )}
                  </Button>
                </div>
              </ReportCard>
            </div>
          </TabsContent>

          {/* CSV Export Tab */}
          <TabsContent value="csv" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Fighters CSV */}
              <div className="bg-ba-surface border border-ba-border p-5 rounded-sm hover:border-red-600/30 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-red-600/10 border border-red-600/20 rounded-sm flex items-center justify-center">
                    <Users className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white">Fighters</h3>
                    <p className="text-zinc-500 text-xs">All fighter profiles</p>
                  </div>
                </div>
                <Button
                  onClick={() => downloadCSV(exportFightersCSV(), 'fighters_export.csv', 'fighters')}
                  disabled={csvDownloading.fighters}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-body text-sm"
                  data-testid="export-fighters-csv"
                >
                  {csvDownloading.fighters ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Table className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </Button>
              </div>

              {/* Assessments CSV */}
              <div className="bg-ba-surface border border-ba-border p-5 rounded-sm hover:border-blue-600/30 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-600/10 border border-blue-600/20 rounded-sm flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white">Assessments</h3>
                    <p className="text-zinc-500 text-xs">Skills & ratings history</p>
                  </div>
                </div>
                <Button
                  onClick={() => downloadCSV(exportAssessmentsCSV(), 'assessments_export.csv', 'assessments')}
                  disabled={csvDownloading.assessments}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-body text-sm"
                  data-testid="export-assessments-csv"
                >
                  {csvDownloading.assessments ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Table className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </Button>
              </div>

              {/* Fights CSV */}
              <div className="bg-ba-surface border border-ba-border p-5 rounded-sm hover:border-yellow-600/30 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-yellow-600/10 border border-yellow-600/20 rounded-sm flex items-center justify-center">
                    <Swords className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white">Fights</h3>
                    <p className="text-zinc-500 text-xs">Fight history & results</p>
                  </div>
                </div>
                <Button
                  onClick={() => downloadCSV(exportFightsCSV(), 'fights_export.csv', 'fights')}
                  disabled={csvDownloading.fights}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-body text-sm"
                  data-testid="export-fights-csv"
                >
                  {csvDownloading.fights ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Table className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </Button>
              </div>

              {/* Training Camps CSV */}
              <div className="bg-ba-surface border border-ba-border p-5 rounded-sm hover:border-purple-600/30 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-600/10 border border-purple-600/20 rounded-sm flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white">Training Camps</h3>
                    <p className="text-zinc-500 text-xs">Camp schedules & details</p>
                  </div>
                </div>
                <Button
                  onClick={() => downloadCSV(exportCampsCSV(), 'training_camps_export.csv', 'camps')}
                  disabled={csvDownloading.camps}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-body text-sm"
                  data-testid="export-camps-csv"
                >
                  {csvDownloading.camps ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Table className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </Button>
              </div>

              {/* Weight Entries CSV */}
              <div className="bg-ba-surface border border-ba-border p-5 rounded-sm hover:border-cyan-600/30 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-cyan-600/10 border border-cyan-600/20 rounded-sm flex items-center justify-center">
                    <Scale className="w-5 h-5 text-cyan-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white">Weight Entries</h3>
                    <p className="text-zinc-500 text-xs">Weight tracking history</p>
                  </div>
                </div>
                <Button
                  onClick={() => downloadCSV(exportWeightCSV(), 'weight_entries_export.csv', 'weight')}
                  disabled={csvDownloading.weight}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-body text-sm"
                  data-testid="export-weight-csv"
                >
                  {csvDownloading.weight ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Table className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </Button>
              </div>

              {/* Members CSV */}
              <div className="bg-ba-surface border border-ba-border p-5 rounded-sm hover:border-green-600/30 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-600/10 border border-green-600/20 rounded-sm flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white">Members</h3>
                    <p className="text-zinc-500 text-xs">Gym member roster</p>
                  </div>
                </div>
                <Button
                  onClick={() => downloadCSV(exportMembersCSV(), 'members_export.csv', 'members')}
                  disabled={csvDownloading.members}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-body text-sm"
                  data-testid="export-members-csv"
                >
                  {csvDownloading.members ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Table className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </Button>
              </div>

              {/* Attendance CSV with date range */}
              <div className="bg-ba-surface border border-ba-border p-5 rounded-sm hover:border-emerald-600/30 transition-all md:col-span-2 lg:col-span-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-600/10 border border-emerald-600/20 rounded-sm flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-white">Attendance Records</h3>
                      <p className="text-zinc-500 text-xs">Export with date range filter</p>
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col sm:flex-row gap-3 sm:items-end sm:justify-end">
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        value={csvAttendanceStart}
                        onChange={(e) => setCsvAttendanceStart(e.target.value)}
                        className="h-9 bg-ba-black border-ba-border text-sm w-36"
                      />
                      <span className="text-zinc-500 self-center">to</span>
                      <Input
                        type="date"
                        value={csvAttendanceEnd}
                        onChange={(e) => setCsvAttendanceEnd(e.target.value)}
                        className="h-9 bg-ba-black border-ba-border text-sm w-36"
                      />
                    </div>
                    <Button
                      onClick={() => downloadCSV(
                        exportAttendanceCSV(csvAttendanceStart, csvAttendanceEnd), 
                        `attendance_${csvAttendanceStart}_to_${csvAttendanceEnd}.csv`, 
                        'attendance'
                      )}
                      disabled={csvDownloading.attendance}
                      className="bg-zinc-800 hover:bg-zinc-700 text-white font-body text-sm"
                      data-testid="export-attendance-csv"
                    >
                      {csvDownloading.attendance ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Table className="w-4 h-4 mr-2" />
                      )}
                      Export Attendance CSV
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* CSV Info Banner */}
            <div className="bg-ba-surface border border-ba-border p-4 rounded-sm flex items-center gap-4 mt-6">
              <FileSpreadsheet className="w-8 h-8 text-zinc-500 flex-shrink-0" />
              <div>
                <p className="text-zinc-300 font-body text-sm">
                  CSV files can be opened in Excel, Google Sheets, or any spreadsheet application. 
                  Use these exports for analysis, backups, or sharing data with third parties.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Info Banner */}
        {activeTab === 'pdf' && (
          <div className="bg-ba-surface border border-ba-border p-4 rounded-sm flex items-center gap-4">
            <FileText className="w-8 h-8 text-zinc-500 flex-shrink-0" />
            <div>
              <p className="text-zinc-300 font-body text-sm">
                All reports are generated as PDF files with Black Arrow MMA branding. 
                Reports include the latest data from your gym.
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
